import { State } from '../State';
import {
  RunningABTests,
  ABTestID,
  ABTestBucket,
  DynamicABTestID,
  DynamicABTestBucketID,
  DynamicABTestBucket,
} from '../ruleset/ab';
import { teaHash } from '@play-co/replicant';
import ab from '../ruleset/ab';
import { Tuple } from '../utils/types';

export function getRunningTests(state: State) {
  // This cast is only relatively safe,
  // because an action is called at game start that updates state.
  return state.abTests as RunningABTests;
}

export function getTestBucket<T extends ABTestID>(state: State, test: T) {
  if (!(test in state.abTests)) {
    return null;
  }

  return state.abTests[test] as ABTestBucket<T>;
}

/**
 * Get replicant dynamic test bucket ID
 * That ab test controlled by CSTool
 */
export function getDynamicTestBucket<T extends DynamicABTestID>(
  state: { ruleset: State['ruleset'] },
  test: T,
) {
  if (!state.ruleset || !(test in state.ruleset.abTests)) {
    return null;
  }

  return state.ruleset.abTests[test]!.bucketId as DynamicABTestBucketID<T>;
}

export function isDynamicTestInBucket<T extends DynamicABTestID>(
  state: Pick<State, 'ruleset'>,
  test: T,
  ...buckets: Tuple<DynamicABTestBucketID<T>, number>
) {
  const bucket = getDynamicTestBucket(state, test);
  if (!bucket) {
    return false;
  }

  return buckets.includes(bucket);
}

/**
 * Predict receiver bucket for dynamic ab test
 * See discussion: https://playcoinc.slack.com/archives/C02ALN39W/p1656044893407159
 * Watch for ticket: https://github.com/play-co/replicant/issues/1200
 */
export function getDynamicReceiverBucket<TABTestID extends DynamicABTestID>(
  testId: TABTestID,
  userId: string,
): DynamicABTestBucket<TABTestID> {
  const buckets = ab.dynamicAB[testId]!.buckets;

  const roll = teaHash(`${userId}_${testId}`, 0);
  const index = Math.floor(roll * buckets.length);

  return buckets[index]!.id;
}

export function isDynamicReceiverInBucket<T extends DynamicABTestID>(
  test: T,
  userId: string,
  ...buckets: DynamicABTestBucketID<T>[]
) {
  return buckets.includes(getDynamicReceiverBucket(test, userId));
}

export function getReceiverBucket<T extends ABTestID>(
  test: T,
  userId: string,
): ABTestBucket<T> {
  const buckets = ab.config[test].buckets;
  const roll = Math.min(
    buckets.length - 1,
    Math.floor(teaHash(test + userId) * buckets.length),
  );

  return buckets[roll].id;
}

export function isTestInBucket<T extends ABTestID>(
  state: State,
  test: T,
  bucket: ABTestBucket<T>,
) {
  return getTestBucket(state, test) === bucket;
}

export function isTestInBuckets<T extends ABTestID>(
  state: State,
  test: T,
  buckets: ABTestBucket<T>[],
) {
  const bucket = getTestBucket(state, test);
  if (!bucket) {
    return false;
  }

  return buckets.includes(bucket);
}
